import React, { type ReactNode, useEffect, useMemo, forwardRef } from 'react';
import camelCase from 'lodash/camelCase';
import { graphql, useFragment } from 'react-relay';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useTabShortcuts } from '@atlassian/jira-horizontal-nav-common/src/common/tab-shortcut-context/index.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import type { DefaultTab_tab$key } from '@atlassian/jira-relay/src/__generated__/DefaultTab_tab.graphql';
import type { DefaultTab_container$key } from '@atlassian/jira-relay/src/__generated__/DefaultTab_container.graphql';
import { useNavigationItemAnalytics } from '@atlassian/jira-horizontal-nav-common/src/common/analytics.tsx';
import { useIsTabSelected } from '@atlassian/navigation-kit-ui-tab';
import { TabWithMenu } from './TabWithMenu.tsx';

type Props = {
	tabFragment: DefaultTab_tab$key;
	containerFragment: DefaultTab_container$key;
	position: number;
	icon: ReactNode;
	isRenaming: boolean;
	onRenamingChange: (isRenaming: boolean) => void;
	testId?: string;
	elemAfter?: ReactNode;
	beforeItemId: string | null;
	afterItemId: string | null;
};

export const DefaultTab = forwardRef<HTMLElement, Props>(
	(
		{
			tabFragment,
			containerFragment,
			position,
			icon,
			isRenaming,
			onRenamingChange,
			testId,
			elemAfter,
			beforeItemId,
			afterItemId,
		},
		ref,
	) => {
		const { push } = useRouterActions();
		const isSelected = useIsTabSelected();

		const tabData = useFragment(
			graphql`
				fragment DefaultTab_tab on JiraNavigationItem {
					label @required(action: THROW)
					url @required(action: THROW)
					typeKey @required(action: THROW)
					...TabWithMenu_tab
				}
			`,
			tabFragment,
		);
		const containerData = useFragment(
			graphql`
				fragment DefaultTab_container on JiraContainerNavigationResult {
					...TabWithMenu_container
				}
			`,
			containerFragment,
		);

		const triggerAnalytics = useNavigationItemAnalytics(
			useMemo(
				() => ({
					itemId: 'undefined',
					itemType: camelCase(tabData.typeKey),
					navState: V4,
					stage: getNav4Rollout().stage,
					navigationLayer: CONTAINER,
					navigationContainer: TAB,
					isInMore: false,
				}),
				[tabData.typeKey],
			),
		);

		const { registerTab } = useTabShortcuts();

		useEffect(
			() =>
				registerTab({
					index: position,
					callback: () => {
						if (!isSelected) {
							push(tabData.url);
						}
					},
					label: tabData.label,
				}),
			[registerTab, tabData, position, push, isSelected],
		);

		return (
			<TabWithMenu
				elemBefore={icon}
				elemAfter={elemAfter}
				href={tabData.url}
				tabFragment={tabData}
				containerFragment={containerData}
				onClick={triggerAnalytics}
				isRenaming={isRenaming}
				onRenamingChange={onRenamingChange}
				testId={testId}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
				position={position}
				type="router-link"
				ref={ref}
			/>
		);
	},
);
